import {
  Button,
  Card,
  CardContent,
  DateInput,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  Input,
  MultipleSelect,
  SimpleSelect,
} from '@kalos/ui';
import { type FC, useMemo } from 'react';
import { useFormContext } from 'react-hook-form';

import { OPTION_ALL } from '../../../../../constants';
import { useTimesheetDepartmentListQuery } from '../../../../../hooks/react-query/useTimesheetDepartmentQuery';
import { useBatchUserQuery } from '../../../../../hooks/react-query/useUserQuery';
import { type JobNoteFilterValues } from './jobNoteFiltersUtils';

interface JobNoteFiltersProps {
  availableCreatorIds?: number[];
  availableEventIds?: number[];
  showEventIdFilter?: boolean;
  onReset: () => void;
  /**
   * Render only the fields, for use inside a FilterSheet (which owns the form and the Reset
   * control; search stays on the screen). Without it, renders the card with its own form.
   */
  inSheet?: boolean;
}

const useJobNoteFiltersFormContext = () => {
  return useFormContext<JobNoteFilterValues>();
};

export const JobNoteFilters: FC<JobNoteFiltersProps> = ({
  availableCreatorIds = [],
  availableEventIds = [],
  showEventIdFilter = false,
  onReset,
  inSheet = false,
}) => {
  const form = useJobNoteFiltersFormContext();

  const timesheetDepartmentQuery = useTimesheetDepartmentListQuery({
    filter: { isActive: 1 },
    select(data) {
      const results = data.results;

      const options = results.map((department) => ({
        label: `${department.value} | ${department.description}`,
        value: department.id.toString(),
      }));

      options.unshift({ label: OPTION_ALL, value: '0' });
      return options;
    },
  });

  // Query for all technicians
  const techniciansQuery = useBatchUserQuery({
    filters: {
      isEmployee: 1,
      overrideLimit: true,
      orderBy: 'user_lastname',
      orderDir: 'ASC',
    },
  });

  // Filter technicians to only those who have created notes
  const availableTechnicians = useMemo(() => {
    if (!techniciansQuery.data?.results || availableCreatorIds.length === 0) {
      return [];
    }

    return techniciansQuery.data.results
      .filter((tech) => availableCreatorIds.includes(tech.id))
      .map((tech) => ({
        label: `${tech.firstname} ${tech.lastname}`,
        value: tech.id.toString(),
      }));
  }, [techniciansQuery.data?.results, availableCreatorIds]);

  // Build job options from available event IDs
  const jobOptions = useMemo(() => {
    if (availableEventIds.length === 0) {
      return [];
    }

    const options = availableEventIds.map((eventId) => ({
      label: `Job #${eventId}`,
      value: eventId.toString(),
    }));

    options.unshift({ label: OPTION_ALL, value: '0' });
    return options;
  }, [availableEventIds]);

  const startDate = form.watch('startDate');
  const endDate = form.watch('endDate');

  const fields = (
    <>
      <div
        className={`grid grid-cols-1 gap-4 ${showEventIdFilter ? 'md:grid-cols-4' : 'md:grid-cols-2'}`}
      >
        {!inSheet && (
          <FormField
            control={form.control}
            name="searchText"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Search</FormLabel>
                <FormControl>
                  <Input {...field} className="bg-card" placeholder="Search text" />
                </FormControl>
              </FormItem>
            )}
          />
        )}

        {showEventIdFilter && (
          <FormField
            control={form.control}
            name="departmentId"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Department</FormLabel>
                <FormControl>
                  <SimpleSelect
                    disabled={!timesheetDepartmentQuery.isSuccess}
                    placeholder="-- All --"
                    selectedValue={(field.value || '').toString()}
                    onChange={(value) => {
                      field.onChange(Number(value));
                    }}
                    values={timesheetDepartmentQuery.data || []}
                  />
                </FormControl>
              </FormItem>
            )}
          />
        )}

        <FormField
          control={form.control}
          name="createdByIds"
          render={({ field }) => {
            return (
              <FormItem>
                <FormLabel>Created By</FormLabel>
                <FormControl>
                  <MultipleSelect
                    options={availableTechnicians}
                    selected={field.value.map((id) => id.toString())}
                    onChange={(values) => {
                      field.onChange(values.map((v) => Number(v)));
                    }}
                    placeholder="Select technicians..."
                    loading={techniciansQuery.isLoading}
                    disabled={field.disabled || availableTechnicians.length === 0}
                  />
                </FormControl>
              </FormItem>
            );
          }}
        />

        {showEventIdFilter && (
          <FormField
            control={form.control}
            name="eventId"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Job</FormLabel>
                <FormControl>
                  <SimpleSelect
                    disabled={jobOptions.length === 0}
                    placeholder="-- All Jobs --"
                    selectedValue={(field.value || '').toString()}
                    onChange={(value) => {
                      field.onChange(Number(value));
                    }}
                    values={jobOptions}
                  />
                </FormControl>
              </FormItem>
            )}
          />
        )}
      </div>

      <div className="space-y-2">
        <div className="grid grid-cols-1 gap-4 md:grid-cols-4">
          <FormField
            control={form.control}
            name="startDate"
            render={({ field }) => (
              <FormItem>
                <FormLabel>From Date</FormLabel>
                <FormControl>
                  <DateInput value={field.value} onChange={field.onChange} />
                </FormControl>
              </FormItem>
            )}
          />

          <FormField
            control={form.control}
            name="endDate"
            render={({ field }) => (
              <FormItem>
                <FormLabel>To Date</FormLabel>
                <FormControl>
                  <DateInput value={field.value} onChange={field.onChange} />
                </FormControl>
              </FormItem>
            )}
          />

          <FormField
            control={form.control}
            name="startTime"
            render={({ field }) => (
              <FormItem>
                <FormLabel>From Time</FormLabel>
                <FormControl>
                  <Input
                    type="time"
                    disabled={!startDate}
                    value={field.value || ''}
                    onChange={(e) => field.onChange(e.target.value || undefined)}
                    step="300"
                    placeholder={!startDate ? 'Select date first' : undefined}
                    className="cursor-pointer"
                    onClick={(e) => {
                      if (startDate) {
                        e.currentTarget.showPicker?.();
                      }
                    }}
                  />
                </FormControl>
              </FormItem>
            )}
          />

          <FormField
            control={form.control}
            name="endTime"
            render={({ field }) => (
              <FormItem>
                <FormLabel>To Time</FormLabel>
                <FormControl>
                  <Input
                    type="time"
                    disabled={!endDate}
                    value={field.value || ''}
                    onChange={(e) => field.onChange(e.target.value || undefined)}
                    step="300"
                    placeholder={!endDate ? 'Select date first' : undefined}
                    className="cursor-pointer"
                    onClick={(e) => {
                      if (endDate) {
                        e.currentTarget.showPicker?.();
                      }
                    }}
                  />
                </FormControl>
              </FormItem>
            )}
          />
        </div>
        {(!startDate || !endDate) && (
          <p className="text-muted-foreground text-xs italic">
            Note: Time filters require their corresponding date to be selected first
          </p>
        )}
      </div>
    </>
  );

  if (inSheet) return <div className="space-y-4">{fields}</div>;

  return (
    <Card className="bg-card shadow-sm">
      <CardContent className="p-4">
        <form className="space-y-4">
          {fields}
          <div className="flex justify-end">
            <Button type="button" variant="outline" onClick={onReset}>
              Reset Filters
            </Button>
          </div>
        </form>
      </CardContent>
    </Card>
  );
};
